import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Lock, LogOut, RefreshCw, Search, FileSpreadsheet, FileText, Mail, Ticket, Trash2, ChevronDown, ChevronRight, Save, Vote, Plus, X, DollarSign, Image as ImageIcon, UploadCloud, Instagram, Pencil, BookOpen, IdCard, BadgeCheck, FolderDown, Printer } from 'lucide-react';
import { AdminRoster } from '../components/admin/AdminRoster';
import { EditPaymentModal, totalDue, amountReceived } from '../components/admin/EditPaymentModal';
import { EditDetailsModal } from '../components/admin/EditDetailsModal';
import { AdminDigitalJournal } from '../components/admin/AdminDigitalJournal';
import { toast } from 'sonner';
import { PAYMENT_METHODS, CHAPTERS } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'cbtu_admin_pw';
const STATUSES = ['Pending', 'Paid', 'Partial', 'Refunded'];

const MANUAL_SALE_CATEGORIES = [
  'Platinum Sponsorship',
  'Gold Sponsorship',
  'Silver Sponsorship',
  'Bronze Sponsorship',
  'Presenting Sponsorship',
  'Full Page Ad',
  'Half Page Ad',
  'Quarter Page Ad',
  'Business Card Ad',
  'Banquet Ticket',
  'Other',
];

const DEFAULT_MANUAL_FORM = {
  name: '',
  email: '',
  chapter: '',
  category: 'Platinum Sponsorship',
  quantity: 1,
  total_paid: '',
  payment_method: 'Intuit / QuickBooks',
  payment_status: 'Paid',
  notes: '',
};

const STATUS_STYLE = {
  Pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
  Paid: 'bg-green-500/25 text-green-300 border-green-500/50',
  Partial: 'bg-orange-500/20 text-orange-300 border-orange-500/40',
  Refunded: 'bg-red-500/20 text-red-300 border-red-500/40',
};

function PaidLine({ r }) {
  const received = amountReceived(r);
  const balance = totalDue(r) - received;
  return (
    <div className="text-[10px] font-sans tracking-normal mt-0.5 whitespace-nowrap" data-testid={`paid-line-${r.id}`}>
      <span className="text-green-300">Rec'd ${received}</span>
      {balance > 0 && <span className="text-orange-300"> · Bal ${balance}</span>}
    </div>
  );
}

function RosterBadge({ match, id }) {
  return match ? (
    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-heading tracking-widest text-green-300 bg-green-500/10 border border-green-500/40 px-1.5 py-0.5 rounded-sm" title={`${match.name} · ${match.chapter} · ${match.membership}`} data-testid={`roster-badge-${id}`}>
      <BadgeCheck size={11} /> DUES-PAYING MEMBER
    </div>
  ) : (
    <div className="mt-1 inline-flex items-center text-[10px] font-heading tracking-widest text-cbtu-muted border border-gold/20 px-1.5 py-0.5 rounded-sm" data-testid={`roster-badge-${id}`}>NOT ON ROSTER</div>
  );
}

function EditBtn({ r, onClick }) {
  return (
    <button onClick={() => onClick(r)} data-testid={`edit-payment-btn-${r.id}`} className="p-1.5 rounded-sm text-gold hover:bg-gold/15 mr-1" aria-label="Update payment" title="Update payment">
      <Pencil size={14} />
    </button>
  );
}

export default function Admin() {
  const [password, setPassword] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [tab, setTab] = useState('registration');
  const [items, setItems] = useState([]);
  const [rosterLoaded, setRosterLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({}); // { id: true }
  const [drafts, setDrafts] = useState({}); // { id: { payment_method, payment_status, total_paid, notes } }
  const [saving, setSaving] = useState({}); // { id: bool }
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualForm, setManualForm] = useState(DEFAULT_MANUAL_FORM);
  const [submittingManual, setSubmittingManual] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingDetails, setEditingDetails] = useState(null);
  const [printChapter, setPrintChapter] = useState('');
  // Chapter photo uploader state
  const [photoChapter, setPhotoChapter] = useState(CHAPTERS[0]?.slug || 'jacksonville');
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoFiles, setPhotoFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState('');
  const [photoInstagram, setPhotoInstagram] = useState('');
  const [photoHandle, setPhotoHandle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [chapterPhotos, setChapterPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);

  // When chapter changes, pre-fill Instagram defaults from mock CHAPTERS if the admin left them blank
  useEffect(() => {
    const c = CHAPTERS.find((x) => x.slug === photoChapter);
    const ig = c?.socials?.instagram || '';
    setPhotoInstagram(ig);
    // Guess a handle like @CBTU_jax from the URL slug after instagram.com/
    const m = ig.match(/instagram\.com\/([a-zA-Z0-9._-]+)/i);
    setPhotoHandle(m ? `@${m[1]}` : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoChapter]);

  const loadChapterPhotos = async (pw) => {
    setLoadingPhotos(true);
    try {
      const r = await axios.get(`${API}/admin/chapter-photos`, { headers: { 'X-Admin-Password': pw || password } });
      setChapterPhotos(r.data.items || []);
    } catch (err) {
      toast.error('Could not load photos.');
    } finally {
      setLoadingPhotos(false);
    }
  };

  const submitPhotoUpload = async () => {
    if (!photoFiles.length) { toast.error('Pick at least one photo.'); return; }
    if (!photoChapter) { toast.error('Pick a chapter.'); return; }
    setUploading(true);
    let ok = 0;
    const failed = [];
    for (let i = 0; i < photoFiles.length; i += 1) {
      setUploadProgress(`${i + 1} of ${photoFiles.length}`);
      const fd = new FormData();
      fd.append('chapter_slug', photoChapter);
      fd.append('caption', photoCaption);
      fd.append('instagram', photoInstagram);
      fd.append('handle', photoHandle);
      fd.append('file', photoFiles[i]);
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/admin/chapter-photo/upload`, fd, {
          headers: { 'X-Admin-Password': password, 'Content-Type': 'multipart/form-data' },
        });
        ok += 1;
      } catch (err) {
        failed.push(`${photoFiles[i].name}: ${err?.response?.data?.detail || 'failed'}`);
      }
    }
    if (ok) toast.success(`${ok} photo${ok === 1 ? '' : 's'} uploaded.`);
    if (failed.length) toast.error(`${failed.length} failed. ${failed.slice(0, 3).join(' · ')}`);
    setPhotoCaption('');
    setPhotoFiles([]);
    const fi = document.getElementById('chapter-photo-file-input');
    if (fi) fi.value = '';
    setUploading(false);
    setUploadProgress('');
    await loadChapterPhotos();
  };

  const deleteChapterPhoto = async (p) => {
    if (!window.confirm(`Delete photo "${p.caption || p.id}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/chapter-photo/${p.id}`, { headers: { 'X-Admin-Password': password } });
      toast.success('Photo deleted.');
      await loadChapterPhotos();
    } catch (err) {
      toast.error('Could not delete photo.');
    }
  };

  const submitManualSale = async () => {
    const f = manualForm;
    if (!f.name.trim()) { toast.error('Buyer name is required.'); return; }
    if (!f.category) { toast.error('Please pick an item category.'); return; }
    const amt = parseInt(f.total_paid, 10);
    if (!Number.isFinite(amt) || amt < 0) { toast.error('Enter a valid amount paid.'); return; }
    setSubmittingManual(true);
    try {
      await axios.post(`${API}/admin/manual-sale`, {
        ...f,
        quantity: parseInt(f.quantity, 10) || 1,
        total_paid: amt,
      }, { headers: { 'X-Admin-Password': password } });
      toast.success('Sale recorded.');
      setShowManualModal(false);
      setManualForm(DEFAULT_MANUAL_FORM);
      load(password, tab);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record sale.');
    } finally {
      setSubmittingManual(false);
    }
  };

  const load = useCallback(async (pw, type) => {
    if (!pw) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/submissions`, {
        params: { type },
        headers: { 'X-Admin-Password': pw },
      });
      setItems(res.data.items || []);
      setRosterLoaded(!!res.data.roster_loaded);
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, pw);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error('Incorrect password.');
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
        setPassword('');
      } else {
        toast.error('Could not load submissions.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (password) load(password, tab); }, [password, tab, load]);

  useEffect(() => {
    if (tab === 'photos' && password) loadChapterPhotos(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, password]);

  const submitLogin = (e) => { e.preventDefault(); if (pwInput) setPassword(pwInput); };
  const logout = () => { sessionStorage.removeItem(STORAGE_KEY); setPassword(''); setAuthed(false); setItems([]); setPwInput(''); };

  const download = (fmt) => {
    if (!password) return;
    const url = `${API}/admin/submissions.${fmt}?type=${tab}&password=${encodeURIComponent(password)}`;
    window.open(url, '_blank');
  };

  const filtered = items.filter((i) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return Object.values(i).some((v) => String(v).toLowerCase().includes(q));
  });

  const fmtDate = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  const getDraft = (r) => drafts[r.id] || {
    payment_method: r.payment_method || '',
    payment_status: r.payment_status || 'Pending',
    total_paid: r.total_paid ?? r.fee ?? 0,
    notes: r.notes || '',
  };
  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...getDraft(items.find((i) => i.id === id) || {}), ...(d[id] || {}), ...patch } }));

  const quickUpdate = async (r, patch) => {
    setSaving((s) => ({ ...s, [r.id]: true }));
    try {
      const res = await axios.patch(`${API}/admin/submissions/${r.id}`, patch, { headers: { 'X-Admin-Password': password } });
      setItems((it) => it.map((x) => (x.id === r.id ? { ...x, ...res.data.item } : x)));
      setDrafts((d) => { const c = { ...d }; delete c[r.id]; return c; });
      toast.success('Updated.');
    } catch {
      toast.error('Could not save changes.');
    } finally {
      setSaving((s) => ({ ...s, [r.id]: false }));
    }
  };

  const saveDraft = async (r) => {
    const d = getDraft(r);
    await quickUpdate(r, { notes: d.notes });
  };

  const savePayment = async (patch) => {
    await quickUpdate(editing, patch);
    setEditing(null);
  };

  const saveDetails = async (patch) => {
    await quickUpdate(editingDetails, patch);
    setEditingDetails(null);
  };

  const del = async (r) => {
    if (!window.confirm(`Delete submission from ${r.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/submissions/${r.id}`, { headers: { 'X-Admin-Password': password } });
      setItems((it) => it.filter((x) => x.id !== r.id));
      toast.success('Deleted.');
    } catch {
      toast.error('Could not delete.');
    }
  };

  if (!authed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 fade-in-up">
        <form onSubmit={submitLogin} className="card-dark rounded-md p-8 w-full max-w-md">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-full bg-cbtu-green/50 border border-gold/40"><Lock className="text-gold" size={22} /></div>
            <div>
              <div className="chip mb-1">RESTRICTED</div>
              <h1 className="font-display text-2xl text-cbtu-cream">Admin Access</h1>
            </div>
          </div>
          <p className="text-cbtu-text/85 text-sm mb-4">Enter the CBTU Region III admin password to view, edit, and download submissions.</p>
          <label className="block mb-4">
            <span className="block text-xs tracking-widest text-gold font-heading mb-1">Password</span>
            <input type="password" value={pwInput} onChange={(e) => setPwInput(e.target.value)} autoFocus className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none focus:ring-2 focus:ring-gold/40" />
          </label>
          <button type="submit" className="gold-button w-full py-3 rounded-sm inline-flex items-center justify-center gap-2"><Lock size={14} /> Sign in</button>
        </form>
      </div>
    );
  }

  const isReg = tab === 'registration';
  const isVoter = tab === 'voter_outreach';
  const isContact = tab === 'contact';
  const isSales = tab === 'sales';
  const isPhotos = tab === 'photos';
  const isJournal = tab === 'journal';
  const isRoster = tab === 'roster';
  const noTable = isPhotos || isJournal || isRoster;

  return (
    <div className="fade-in-up">
      <section className="banner-bg section-noise">
        <div className="max-w-7xl mx-auto px-4 py-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="chip mb-2">ADMIN</div>
            <h1 className="font-display text-3xl md:text-4xl text-cbtu-cream">Submissions <span className="gold-text-fill">Dashboard</span></h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(password, tab)} className="ghost-button px-4 py-2 rounded-sm text-xs inline-flex items-center gap-2"><RefreshCw size={13} /> Refresh</button>
            <button onClick={logout} className="ghost-button px-4 py-2 rounded-sm text-xs inline-flex items-center gap-2"><LogOut size={13} /> Sign out</button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setTab('registration')} data-testid="admin-tab-registrations" className={`px-4 py-2 rounded-sm text-xs font-heading tracking-widest inline-flex items-center gap-2 transition-colors ${isReg ? 'bg-gold text-cbtu-black' : 'border border-gold/40 text-gold hover:bg-gold/10'}`}>
              <Ticket size={13} /> REGISTRATIONS
            </button>
            <button onClick={() => setTab('contact')} data-testid="admin-tab-contacts" className={`px-4 py-2 rounded-sm text-xs font-heading tracking-widest inline-flex items-center gap-2 transition-colors ${isContact ? 'bg-gold text-cbtu-black' : 'border border-gold/40 text-gold hover:bg-gold/10'}`}>
              <Mail size={13} /> CONTACT MESSAGES
            </button>
            <button onClick={() => setTab('voter_outreach')} data-testid="admin-tab-voter" className={`px-4 py-2 rounded-sm text-xs font-heading tracking-widest inline-flex items-center gap-2 transition-colors ${isVoter ? 'bg-gold text-cbtu-black' : 'border border-gold/40 text-gold hover:bg-gold/10'}`}>
              <Vote size={13} /> VOTER OUTREACH
            </button>
            <button onClick={() => setTab('sales')} data-testid="admin-tab-sales" className={`px-4 py-2 rounded-sm text-xs font-heading tracking-widest inline-flex items-center gap-2 transition-colors ${isSales ? 'bg-gold text-cbtu-black' : 'border border-gold/40 text-gold hover:bg-gold/10'}`}>
              <DollarSign size={13} /> SPONSORSHIPS & TICKETS
            </button>
            <button onClick={() => setTab('photos')} data-testid="admin-tab-photos" className={`px-4 py-2 rounded-sm text-xs font-heading tracking-widest inline-flex items-center gap-2 transition-colors ${isPhotos ? 'bg-gold text-cbtu-black' : 'border border-gold/40 text-gold hover:bg-gold/10'}`}>
              <ImageIcon size={13} /> CHAPTER PHOTOS
            </button>
            <button onClick={() => setTab('journal')} data-testid="admin-tab-journal" className={`px-4 py-2 rounded-sm text-xs font-heading tracking-widest inline-flex items-center gap-2 transition-colors ${isJournal ? 'bg-gold text-cbtu-black' : 'border border-gold/40 text-gold hover:bg-gold/10'}`}>
              <BookOpen size={13} /> DIGITAL JOURNAL
            </button>
            <button onClick={() => setTab('roster')} data-testid="admin-tab-roster" className={`px-4 py-2 rounded-sm text-xs font-heading tracking-widest inline-flex items-center gap-2 transition-colors ${isRoster ? 'bg-gold text-cbtu-black' : 'border border-gold/40 text-gold hover:bg-gold/10'}`}>
              <IdCard size={13} /> NATIONAL ROSTER
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isSales && (
              <button
                onClick={() => setShowManualModal(true)}
                data-testid="add-manual-sale-btn"
                className="bg-[#2ca01c] hover:bg-[#248a16] text-white px-4 py-2 rounded-sm text-xs font-heading tracking-widest inline-flex items-center gap-2 transition-colors"
              >
                <Plus size={13} /> ADD SALE
              </button>
            )}
            <button onClick={() => download('xlsx')} className={`gold-button px-4 py-2 rounded-sm text-xs inline-flex items-center gap-2 ${noTable ? 'hidden' : ''}`}><FileSpreadsheet size={13} /> Download Excel</button>
            {tab === 'registration' && (
              <a href={`${API}/admin/registration-forms.zip?password=${encodeURIComponent(password)}`} className="bg-[#2ca01c] hover:bg-[#248a16] text-white px-4 py-2 rounded-sm text-xs inline-flex items-center gap-2 font-heading tracking-wider transition-colors" data-testid="download-forms-zip-btn">
                <FolderDown size={13} /> All Registration Forms (Folder)
              </a>
            )}
            {tab === 'registration' && (
              <div className="inline-flex items-stretch rounded-sm border border-gold/60 overflow-hidden" data-testid="print-by-chapter">
                <select value={printChapter} onChange={(e) => setPrintChapter(e.target.value)} className="bg-cbtu-black text-cbtu-cream text-xs px-2 outline-none" data-testid="print-chapter-select" aria-label="Chapter to print">
                  <option value="">All chapters (sorted by chapter)</option>
                  {[...new Set(items.map((r) => (r.chapter || '').trim() || 'No Chapter Listed'))].sort().map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <a href={`${API}/admin/registration-forms.pdf?password=${encodeURIComponent(password)}${printChapter ? `&chapter=${encodeURIComponent(printChapter)}` : ''}`} target="_blank" rel="noreferrer" className="bg-[#1b4023] hover:bg-[#245530] text-gold px-4 py-2 text-xs inline-flex items-center gap-2 font-heading tracking-wider transition-colors" data-testid="print-all-forms-btn">
                  <Printer size={13} /> Print {printChapter || 'All'}
                </a>
              </div>
            )}
            <button onClick={() => download('csv')} className={`ghost-button px-4 py-2 rounded-sm text-xs inline-flex items-center gap-2 ${noTable ? 'hidden' : ''}`}><FileText size={13} /> Download CSV</button>
          </div>
        </div>

        <div className={`relative mb-4 max-w-md ${noTable ? 'hidden' : ''}`}>
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={isReg ? 'Search registrations…' : isVoter ? 'Search voter outreach…' : isSales ? 'Search sponsorships & tickets…' : 'Search messages…'} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm pl-9 pr-3 py-2 text-sm text-cbtu-cream outline-none" />
        </div>

        {isJournal && <AdminDigitalJournal password={password} />}
        {isRoster && <AdminRoster password={password} />}

        {isPhotos && (
          <div className="grid lg:grid-cols-[380px_1fr] gap-6">
            {/* Upload form */}
            <div className="card-dark rounded-md p-5 h-fit" data-testid="chapter-photo-uploader">
              <div className="flex items-center gap-2 mb-4">
                <UploadCloud className="text-gold" size={18} />
                <h3 className="font-heading tracking-widest text-gold text-sm">UPLOAD NEW PHOTO</h3>
              </div>
              <div className="space-y-3">
                <label className="block">
                  <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">CHAPTER <span className="text-red-400">*</span></span>
                  <select data-testid="photo-chapter-select" value={photoChapter} onChange={(e) => setPhotoChapter(e.target.value)} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none">
                    <option value="conference-2026">★ 2026 Conference Photo Gallery</option>
                    {CHAPTERS.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">PHOTOS <span className="text-red-400">*</span></span>
                  <input id="chapter-photo-file-input" data-testid="photo-file-input" type="file" multiple accept="image/*,.heic,.heif" onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))} className="w-full text-xs text-cbtu-cream file:bg-gold file:text-cbtu-black file:border-0 file:rounded-sm file:px-3 file:py-2 file:mr-3 file:font-heading file:text-xs file:tracking-widest file:cursor-pointer" />
                  <span className="text-[11px] text-cbtu-muted block mt-1">Select one or many photos at once. iPhone HEIC supported. Max 15 MB each. Auto-rotated &amp; resized.</span>
                  {photoFiles.length > 1 && <span className="text-[11px] text-gold block mt-1" data-testid="photo-selected-count">{photoFiles.length} photos selected</span>}
                </label>
                <label className="block">
                  <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">CAPTION</span>
                  <input data-testid="photo-caption-input" type="text" value={photoCaption} onChange={(e) => setPhotoCaption(e.target.value)} placeholder="e.g. GOTV Rally, Oct 2026" className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none" />
                </label>
                <div className="grid grid-cols-[1fr_120px] gap-2">
                  <label className="block">
                    <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">INSTAGRAM URL</span>
                    <input type="url" value={photoInstagram} onChange={(e) => setPhotoInstagram(e.target.value)} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none" />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">HANDLE</span>
                    <input type="text" value={photoHandle} onChange={(e) => setPhotoHandle(e.target.value)} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none" />
                  </label>
                </div>
                <button onClick={submitPhotoUpload} disabled={uploading || !photoFiles.length} data-testid="photo-upload-btn" className="w-full bg-[#2ca01c] hover:bg-[#248a16] disabled:opacity-60 disabled:cursor-not-allowed text-white font-heading text-xs tracking-widest px-4 py-3 rounded-sm inline-flex items-center justify-center gap-2 transition-colors mt-2">
                  <UploadCloud size={14} /> {uploading ? `UPLOADING ${uploadProgress}…` : `UPLOAD ${photoFiles.length > 1 ? `${photoFiles.length} PHOTOS` : 'PHOTO'}`}
                </button>
              </div>
            </div>

            {/* Gallery of existing photos */}
            <div>
              <div className="flex items-center justify-between mb-3 text-xs">
                <span className="text-gold font-heading tracking-widest">{loadingPhotos ? 'LOADING…' : `${chapterPhotos.length} uploaded photo${chapterPhotos.length === 1 ? '' : 's'}`}</span>
                <button onClick={() => loadChapterPhotos()} className="text-cbtu-muted hover:text-gold inline-flex items-center gap-1 text-[11px]"><RefreshCw size={11} /> REFRESH</button>
              </div>
              {chapterPhotos.length === 0 && !loadingPhotos && (
                <div className="card-dark rounded-md p-10 text-center text-cbtu-muted">
                  No photos uploaded yet. Use the form on the left to add the first one.
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {chapterPhotos.map((p) => {
                  const chapterName = CHAPTERS.find((c) => c.slug === p.chapter_slug)?.name || p.chapter_slug;
                  return (
                    <figure key={p.id} className="card-dark rounded-md overflow-hidden flex flex-col" data-testid={`photo-tile-${p.id}`}>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${p.src}`} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden bg-cbtu-black">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${p.src}`} alt={p.caption || 'Chapter photo'} className="w-full h-full object-cover" loading="lazy" />
                      </a>
                      <figcaption className="p-3 flex-1 flex flex-col gap-2 border-t border-gold/20">
                        <div className="flex items-center gap-1.5 text-[10px] font-heading tracking-widest text-gold"><ImageIcon size={10} /> {chapterName.toUpperCase()}</div>
                        <div className="text-cbtu-cream text-xs leading-snug flex-1">{p.caption || <span className="text-cbtu-muted italic">No caption</span>}</div>
                        <div className="flex items-center justify-between gap-2 text-[10px] text-cbtu-muted">
                          {p.handle ? <span className="inline-flex items-center gap-1"><Instagram size={10} />{p.handle}</span> : <span />}
                          <button onClick={() => deleteChapterPhoto(p)} data-testid={`photo-delete-${p.id}`} className="p-1 rounded-sm text-red-400 hover:bg-red-500/20" aria-label="Delete photo"><Trash2 size={12} /></button>
                        </div>
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className={`card-dark rounded-md overflow-hidden ${noTable ? 'hidden' : ''}`}>
          <div className="flex items-center justify-between px-4 py-3 bg-cbtu-green/40 border-b border-gold/30 text-xs">
            <span className="text-gold font-heading tracking-widest">{loading ? 'LOADING…' : `${filtered.length} of ${items.length} total`}</span>
            {isSales && !loading && (
              <span className="text-cbtu-cream text-xs">
                Grand Total: <span className="font-display gold-text-fill text-lg ml-1">${filtered.reduce((sum, r) => sum + totalDue(r), 0).toLocaleString()}</span>
                <span className="text-cbtu-muted ml-3">Received:</span> <span className="text-green-300 font-display text-lg" data-testid="sales-received-total">${filtered.reduce((sum, r) => sum + amountReceived(r), 0).toLocaleString()}</span>
              </span>
            )}
            <span className="text-cbtu-muted">Newest first</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-cbtu-black text-gold text-xs uppercase tracking-widest border-b border-gold/20">
                {isReg ? (
                  <tr>
                    <th className="px-3 py-3 w-8"></th>
                    <th className="px-3 py-3">Submitted</th>
                    <th className="px-3 py-3">Name</th>
                    <th className="px-3 py-3">Chapter</th>
                    <th className="px-3 py-3">Category</th>
                    <th className="px-3 py-3">Payment</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3 text-right">Total</th>
                    <th className="px-3 py-3 w-16"></th>
                  </tr>
                ) : isVoter ? (
                  <tr>
                    <th className="px-4 py-3">Submitted</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Address</th>
                    <th className="px-4 py-3">DOB</th>
                    <th className="px-4 py-3">State</th>
                    <th className="px-4 py-3 w-16"></th>
                  </tr>
                ) : isSales ? (
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Buyer</th>
                    <th className="px-4 py-3">Chapter</th>
                    <th className="px-4 py-3">Item</th>
                    <th className="px-4 py-3">Qty</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 w-16"></th>
                  </tr>
                ) : (
                  <tr>
                    <th className="px-4 py-3">Submitted</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Topic</th>
                    <th className="px-4 py-3">Message</th>
                    <th className="px-4 py-3 w-16"></th>
                  </tr>
                )}
              </thead>
              <tbody>
                {filtered.length === 0 && !loading && (
                  <tr><td colSpan={isReg ? 9 : isVoter ? 6 : isSales ? 8 : 6} className="px-4 py-10 text-center text-cbtu-muted">
                    No {isReg ? 'registrations' : isVoter ? 'voter outreach records' : isSales ? 'sponsorship or ticket sales' : 'messages'} yet.
                    {isSales && <> Click <span className="text-[#4bd838] font-heading">+ ADD SALE</span> to record a QuickBooks online purchase.</>}
                  </td></tr>
                )}
                {filtered.map((r) => {
                  if (isSales) {
                    const qty = (r.items && r.items[0] && r.items[0].quantity) || 1;
                    const statusCls = STATUS_STYLE[r.payment_status] || 'bg-cbtu-black text-cbtu-muted border-gold/20';
                    return (
                      <tr key={r.id} className="border-b border-gold/10 hover:bg-cbtu-green/10 transition-colors align-top" data-testid={`sale-row-${r.id}`}>
                        <td className="px-4 py-3 text-cbtu-muted whitespace-nowrap">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-3 text-cbtu-cream">
                          <div className="font-medium">{r.name}</div>
                          {r.email && <div className="text-xs text-cbtu-muted"><a href={`mailto:${r.email}`} className="hover:text-gold-bright">{r.email}</a></div>}
                        </td>
                        <td className="px-4 py-3 text-cbtu-cream">{r.chapter || '—'}</td>
                        <td className="px-4 py-3 text-cbtu-cream text-xs">
                          <div>{r.category}</div>
                          {r.notes && <div className="text-cbtu-muted text-[11px] italic mt-0.5">{r.notes}</div>}
                        </td>
                        <td className="px-4 py-3 text-cbtu-cream">×{qty}</td>
                        <td className="px-4 py-3 text-right"><span className="font-display gold-text-fill text-lg">${totalDue(r)}</span><PaidLine r={r} /></td>
                        <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-heading tracking-widest border ${statusCls}`}>{r.payment_status || '—'}</span></td>
                        <td className="px-4 py-3 whitespace-nowrap"><EditBtn r={r} onClick={setEditing} /><button onClick={() => del(r)} className="p-1.5 rounded-sm text-red-400 hover:bg-red-500/20" aria-label="Delete"><Trash2 size={14} /></button></td>
                      </tr>
                    );
                  }
                  if (isVoter) {
                    return (
                      <tr key={r.id} className="border-b border-gold/10 hover:bg-cbtu-green/10 transition-colors align-top" data-testid={`voter-row-${r.id}`}>
                        <td className="px-4 py-3 text-cbtu-muted whitespace-nowrap">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-3 text-cbtu-cream">{r.name}</td>
                        <td className="px-4 py-3 text-cbtu-cream">{r.address}</td>
                        <td className="px-4 py-3 text-cbtu-cream">{r.dob}</td>
                        <td className="px-4 py-3 text-cbtu-cream">{r.state_name} <span className="text-cbtu-muted text-xs">({r.state_code})</span></td>
                        <td className="px-4 py-3"><button onClick={() => del(r)} className="p-1.5 rounded-sm text-red-400 hover:bg-red-500/20" aria-label="Delete"><Trash2 size={14} /></button></td>
                      </tr>
                    );
                  }
                  if (!isReg) {
                    return (
                      <tr key={r.id} className="border-b border-gold/10 hover:bg-cbtu-green/10 transition-colors align-top">
                        <td className="px-4 py-3 text-cbtu-muted whitespace-nowrap">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-3 text-cbtu-cream">{r.name}</td>
                        <td className="px-4 py-3 text-cbtu-cream"><a href={`mailto:${r.email}`} className="hover:text-gold-bright">{r.email}</a></td>
                        <td className="px-4 py-3 text-cbtu-cream">{r.topic}</td>
                        <td className="px-4 py-3 text-cbtu-cream max-w-md whitespace-pre-wrap">{r.message}</td>
                        <td className="px-4 py-3"><button onClick={() => del(r)} className="p-1.5 rounded-sm text-red-400 hover:bg-red-500/20" aria-label="Delete"><Trash2 size={14} /></button></td>
                      </tr>
                    );
                  }
                  const d = getDraft(r);
                  const currentStatus = r.payment_status || 'Pending';
                  const isOpen = !!expanded[r.id];
                  const isSaving = !!saving[r.id];
                  const dirty = !!drafts[r.id];
                  return (
                    <React.Fragment key={r.id}>
                      <tr className="border-b border-gold/10 hover:bg-cbtu-green/10 transition-colors align-top" data-testid={`reg-row-${r.id}`}>
                        <td className="px-3 py-3">
                          <button onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))} className="text-gold" aria-label="Toggle details">
                            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </td>
                        <td className="px-3 py-3 text-cbtu-muted whitespace-nowrap">{fmtDate(r.created_at)}</td>
                        <td className="px-3 py-3 text-cbtu-cream">
                          <div className="font-medium">{r.name}</div>
                          <div className="text-xs text-cbtu-muted"><a href={`mailto:${r.email}`} className="hover:text-gold-bright">{r.email}</a></div>
                          {rosterLoaded && <RosterBadge match={r.roster_match} id={r.id} />}
                        </td>
                        <td className="px-3 py-3 text-cbtu-cream">{r.chapter || '—'}</td>
                        <td className="px-3 py-3 text-cbtu-cream text-xs">{r.category}</td>
                        <td className="px-3 py-3">
                          <select
                            value={d.payment_method}
                            onChange={(e) => quickUpdate(r, { payment_method: e.target.value })}
                            disabled={isSaving}
                            className="bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-2 py-1 text-xs text-cbtu-cream outline-none"
                          >
                            <option value="">— Set —</option>
                            {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-3">
                          <select
                            value={currentStatus}
                            onChange={(e) => quickUpdate(r, { payment_status: e.target.value })}
                            disabled={isSaving}
                            className={`rounded-sm px-2 py-1 text-xs font-heading tracking-wider border outline-none ${STATUS_STYLE[currentStatus] || STATUS_STYLE.Pending}`}
                          >
                            {STATUSES.map((s) => <option key={s} value={s} className="bg-cbtu-black text-cbtu-cream">{s}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-3 text-right text-gold font-display">${totalDue(r)}<PaidLine r={r} /></td>
                        <td className="px-3 py-3 whitespace-nowrap"><a href={`${API}/admin/registration-form/${r.id}.pdf?password=${encodeURIComponent(password)}`} target="_blank" rel="noreferrer" className="inline-flex p-1.5 rounded-sm text-green-300 hover:bg-green-500/15 mr-1" aria-label="View registration form" title="View / print registration form" data-testid={`form-pdf-btn-${r.id}`}><FileText size={14} /></a><button onClick={() => setEditingDetails(r)} data-testid={`edit-details-btn-${r.id}`} className="p-1.5 rounded-sm text-cbtu-cream hover:bg-gold/15 mr-1" aria-label="Edit registration details" title="Edit name, type, chapter, tickets"><IdCard size={14} /></button><EditBtn r={r} onClick={setEditing} /><button onClick={() => del(r)} className="p-1.5 rounded-sm text-red-400 hover:bg-red-500/20" aria-label="Delete"><Trash2 size={14} /></button></td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-gold/10 bg-cbtu-black/60">
                          <td colSpan={9} className="px-6 py-5">
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <div className="text-xs font-heading tracking-widest text-gold mb-2">CONTACT & ITEMS</div>
                                <div className="text-sm text-cbtu-cream space-y-1 mb-4">
                                  <div>Phone: <span className="text-cbtu-muted">{r.phone || '—'}</span></div>
                                  <div>Union: <span className="text-cbtu-muted">{r.union || '—'}</span></div>
                                </div>
                                {r.items && r.items.length > 0 && (
                                  <ul className="space-y-1 text-sm">
                                    {r.items.map((it, idx) => (
                                      <li key={`${it.type || 'item'}-${it.label}-${idx}`} className="flex items-center justify-between border-b border-gold/10 py-1.5">
                                        <span className="text-cbtu-cream">{it.label}{it.quantity > 1 ? ` ×${it.quantity}` : ''}</span>
                                        <span className="text-gold font-display">${it.amount}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <div>
                                <div className="text-xs font-heading tracking-widest text-gold mb-2">EDIT DETAILS</div>
                                <label className="block mb-3">
                                  <span className="block text-[11px] tracking-widest text-cbtu-muted mb-1">INTERNAL NOTES</span>
                                  <textarea rows={4} value={d.notes} onChange={(e) => setDraft(r.id, { notes: e.target.value })} placeholder="e.g., Check received 9/15, Called re: banquet count…" className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none resize-none" />
                                </label>
                                <button onClick={() => saveDraft(r)} disabled={!dirty || isSaving} className="gold-button px-5 py-2 rounded-sm text-xs inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"><Save size={13} /> {isSaving ? 'Saving…' : 'Save Changes'}</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <p className={`text-cbtu-muted text-xs mt-3 ${noTable ? 'hidden' : ''}`}>
          Tip: change <span className="text-gold">Payment</span> or <span className="text-gold">Status</span> from the dropdowns to save instantly. Click the <span className="text-gold">pencil</span> to record a partial or full payment (total due, amount received, balance). Click the arrow to open a row and edit notes. On the <span className="text-gold">Sponsorships & Tickets</span> tab, use <span className="text-[#4bd838]">+ ADD SALE</span> to log a QuickBooks online purchase.
        </p>
      </section>

      {editingDetails && <EditDetailsModal record={editingDetails} saving={!!saving[editingDetails.id]} onClose={() => setEditingDetails(null)} onSave={saveDetails} />}
      {editing && <EditPaymentModal record={editing} saving={!!saving[editing.id]} onClose={() => setEditing(null)} onSave={savePayment} />}

      {showManualModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" role="dialog" aria-modal="true" data-testid="manual-sale-modal">
          <div className="relative w-full max-w-lg card-dark rounded-md border border-gold/40 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gold/30 bg-cbtu-green/40 sticky top-0">
              <div className="flex items-center gap-2">
                <Plus size={16} className="text-gold" />
                <h3 className="font-heading tracking-widest text-gold text-sm">RECORD ONLINE SALE (QUICKBOOKS)</h3>
              </div>
              <button onClick={() => setShowManualModal(false)} className="text-cbtu-muted hover:text-gold" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-cbtu-muted leading-relaxed">
                Use this to log a <span className="text-gold">sponsorship, journal ad, or banquet ticket</span> that was paid for through the Intuit "Buy Now" button. Open your QuickBooks invoice and copy the details here so this appears next to your mail-in registrations.
              </p>
              <label className="block">
                <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">BUYER NAME <span className="text-red-400">*</span></span>
                <input data-testid="manual-name" type="text" value={manualForm.name} onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none" />
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">EMAIL</span>
                  <input data-testid="manual-email" type="email" value={manualForm.email} onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none" />
                </label>
                <label className="block">
                  <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">CHAPTER / UNION</span>
                  <input data-testid="manual-chapter" type="text" value={manualForm.chapter} onChange={(e) => setManualForm({ ...manualForm, chapter: e.target.value })} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none" />
                </label>
              </div>
              <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
                <label className="block">
                  <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">ITEM <span className="text-red-400">*</span></span>
                  <select data-testid="manual-category" value={manualForm.category} onChange={(e) => setManualForm({ ...manualForm, category: e.target.value })} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none">
                    {MANUAL_SALE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">QTY</span>
                  <input data-testid="manual-quantity" type="number" min="1" value={manualForm.quantity} onChange={(e) => setManualForm({ ...manualForm, quantity: e.target.value })} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none" />
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">AMOUNT PAID (USD) <span className="text-red-400">*</span></span>
                  <input data-testid="manual-amount" type="number" min="0" placeholder="e.g. 5000" value={manualForm.total_paid} onChange={(e) => setManualForm({ ...manualForm, total_paid: e.target.value })} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none" />
                </label>
                <label className="block">
                  <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">PAYMENT STATUS</span>
                  <select data-testid="manual-status" value={manualForm.payment_status} onChange={(e) => setManualForm({ ...manualForm, payment_status: e.target.value })} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">PAYMENT METHOD</span>
                <input data-testid="manual-method" type="text" value={manualForm.payment_method} onChange={(e) => setManualForm({ ...manualForm, payment_method: e.target.value })} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none" />
              </label>
              <label className="block">
                <span className="block text-[10px] tracking-widest text-gold font-heading mb-1">NOTES (QBO invoice #, extras, etc.)</span>
                <textarea data-testid="manual-notes" rows={3} value={manualForm.notes} onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })} className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none resize-none" />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gold/30 bg-cbtu-black/60 sticky bottom-0">
              <button onClick={() => setShowManualModal(false)} disabled={submittingManual} className="ghost-button px-4 py-2 rounded-sm text-xs tracking-widest font-heading">CANCEL</button>
              <button onClick={submitManualSale} disabled={submittingManual} data-testid="manual-submit-btn" className="bg-[#2ca01c] hover:bg-[#248a16] disabled:opacity-60 text-white px-5 py-2 rounded-sm text-xs tracking-widest font-heading inline-flex items-center gap-2">
                <Plus size={13} /> {submittingManual ? 'SAVING…' : 'RECORD SALE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
